import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Search, 
  TrendingUp, 
  Activity,
  ExternalLink,
  Loader
} from 'lucide-react';
import { supabase } from '../../supabaseClient';
import { showError } from '../../utils/notificationHandler';

const SearchAnalyticsWidget = ({ onViewFullAnalytics }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const { data: analyticsData, error } = await supabase.rpc('get_search_analytics_dashboard');
      
      if (error) throw error;
      
      setData(analyticsData);
    } catch (error) {
      console.error('Error fetching search analytics:', error);
      showError(error, 'Failed to fetch search analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white overflow-hidden shadow-sm rounded-lg border border-gray-200"
      >
        <div className="p-6 flex items-center justify-center">
          <Loader className="w-6 h-6 text-blue-600 animate-spin" />
          <span className="ml-2 text-gray-600">Loading analytics...</span>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white overflow-hidden shadow-sm rounded-lg border border-gray-200"
    >
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
            <Search className="h-4 w-4 text-gray-500" aria-hidden="true" />
            Search Analytics
          </h3>
          <button
            onClick={onViewFullAnalytics}
            className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 font-medium transition-colors"
          >
            View all
            <ExternalLink className="ml-1 h-3.5 w-3.5" aria-hidden="true" />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {/* Searches Today */}
          <div className="bg-blue-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-600">Searches Today</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {data?.total_searches_today || 0}
                </p>
              </div>
              <Search className="h-8 w-8 text-blue-400" />
            </div>
          </div>

          {/* Success Rate */}
          <div className="bg-green-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-600">Success Rate</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {data?.success_rate_today || 0}%
                </p>
              </div>
              <TrendingUp className="h-8 w-8 text-green-400" />
            </div>
          </div>

          {/* Active Searchers */}
          <div className="bg-purple-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-purple-600">Active Users</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {data?.unique_searchers_today || 0}
                </p>
              </div>
              <Activity className="h-8 w-8 text-purple-400" />
            </div>
          </div>
        </div>

        {/* Top Searched Parts */}
        <div>
          <h4 className="text-sm font-semibold text-gray-700 mb-3">Top Searched Parts Today</h4>
          {data?.top_searched_parts && data.top_searched_parts.length > 0 ? (
            <div className="space-y-2">
              {data.top_searched_parts.slice(0, 5).map((part, index) => (
                <div 
                  key={index} 
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center">
                    <span className="inline-flex items-center justify-center w-6 h-6 bg-blue-100 text-blue-800 rounded-full text-xs font-medium mr-3">
                      {index + 1}
                    </span>
                    <span className="font-medium text-gray-900">{part.search_term}</span>
                  </div>
                  <span className="text-sm text-gray-500">{part.count} searches</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 bg-gray-50 rounded-lg">
              <Search className="w-8 h-8 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-500">No search data available yet</p>
              <p className="text-xs text-gray-400 mt-1">Data will appear as users search</p>
            </div>
          )}
        </div>

        {/* Week Summary */}
        {data?.total_searches_week > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">This Week Total</span>
              <span className="font-semibold text-gray-900">
                {data.total_searches_week} searches
              </span>
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default SearchAnalyticsWidget;
